import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { Company, Paginated } from '@/types/api'

/** Изборът се помнеше тук; старият запис се маха, за да не остане в браузъра. */
const LEGACY_STORAGE_KEY = 'izolacii.company'

/**
 * Едно SPV на сграда е нормата в бранша, затова екраните могат да се стеснят до
 * едно дружество. Всяко зареждане започва от „Всички дружества“ (`activeId ===
 * null`): списъците не се стесняват, а нов запис взима `defaultId`.
 */
export const useCompanies = defineStore('companies', () => {
  const items = ref<Company[]>([])
  const activeId = ref<number | null>(null)
  const loading = ref(false)

  const active = computed(() => items.value.find((company) => company.id === activeId.value) ?? null)
  const operating = computed(() => items.value.find((company) => company.is_operating) ?? null)
  /** Дружеството на нов запис: избраното, а при „Всички“ — оперативното. */
  const defaultId = computed(() => activeId.value ?? operating.value?.id ?? null)
  const options = computed(() => items.value
    .filter((company) => company.status === 'active')
    .sort((a, b) => Number(b.is_operating) - Number(a.is_operating)
      || (b.invoice_count ?? 0) - (a.invoice_count ?? 0)
      || a.name.localeCompare(b.name, 'bg')))

  async function load(force = false): Promise<void> {
    if (loading.value || (items.value.length > 0 && !force)) {
      return
    }
    loading.value = true
    try {
      const { data } = await api.get<Paginated<Company>>(endpoints.companies.list, {
        params: { limit: 200, ordering: 'name' },
      })
      items.value = data.results
    } catch {
      // Без сесия заявката връща 403 и това е нормалното състояние преди вход:
      // прихващачът вече връща човека на екрана за вход. Непрехванатото
      // отхвърляне тук изтичаше като грешка на страницата.
      items.value = []
    } finally {
      loading.value = false
    }
  }

  function setActive(id: number | null): void {
    activeId.value = id
  }

  function reset(): void {
    items.value = []
  }

  return { items, activeId, loading, active, defaultId, options, load, setActive, reset }
})

try {
  localStorage.removeItem(LEGACY_STORAGE_KEY)
} catch {
  // Приватен режим на браузъра — няма какво да се маха.
}
